import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calculator, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  Table,
  type SelectOption,
  type TableColumn,
} from '../../ui';
import { useEstimates } from '../../features/estimation/useEstimates';
import { useRooms } from '../../features/rooms/useRooms';
import { formatOutputValue, headlineOutput } from '../../features/estimation/estimateModel';
import type { EstimateSummary } from '../../features/estimation/types';

/**
 * Estimation landing page — a list of saved estimates with a room filter and a
 * "New estimate" entry into the wizard. Rows open the detail/re-open view.
 * Reaches data only through the useEstimates + useRooms hooks.
 */
export function EstimationPage() {
  const navigate = useNavigate();
  const { summaries, loading, error } = useEstimates();
  const rooms = useRooms();

  const [roomFilter, setRoomFilter] = useState<string>('');

  const roomOptions = useMemo<SelectOption[]>(
    () => rooms.summaries.map((r) => ({ value: r.id, label: r.name })),
    [rooms.summaries],
  );

  const roomNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rooms.summaries) map.set(r.id, r.name);
    return map;
  }, [rooms.summaries]);

  const filtered = useMemo(
    () =>
      roomFilter
        ? summaries.filter((s) => s.roomId === roomFilter)
        : summaries,
    [summaries, roomFilter],
  );

  const columns = useMemo<TableColumn<EstimateSummary>[]>(
    () => [
      {
        key: 'title',
        header: 'Title',
        render: (row) => (
          <span className="font-medium text-zinc-100">{row.title || '—'}</span>
        ),
      },
      {
        key: 'template',
        header: 'Template',
        render: (row) => (
          <span className="text-zinc-400">
            {row.templateId}
          </span>
        ),
      },
      {
        key: 'room',
        header: 'Room',
        render: (row) =>
          row.roomId ? (
            <Badge tone="neutral">
              {roomNameById.get(row.roomId) ?? row.roomId}
            </Badge>
          ) : (
            <span className="text-zinc-600">—</span>
          ),
      },
      {
        key: 'result',
        header: 'Result',
        align: 'right',
        render: (row) => {
          const head = headlineOutput(row.outputs);
          if (!head) return <span className="text-zinc-600">—</span>;
          return (
            <span className="tabular-nums text-zinc-200">
              <span className="text-zinc-500">{head.key}: </span>
              {formatOutputValue(head.value)}
            </span>
          );
        },
      },
      {
        key: 'createdBy',
        header: 'By',
        render: (row) => <span className="text-zinc-400">{row.createdBy}</span>,
      },
    ],
    [roomNameById],
  );

  const filtersActive = !!roomFilter;

  return (
    <div>
      <PageHeader
        icon={<Calculator className="h-5 w-5" />}
        title="Estimation"
        description="Compute estimates in the browser against a configurable template."
        actions={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/estimation/new')}
          >
            New estimate
          </Button>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading estimates" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load estimates"
          description={error}
        />
      ) : summaries.length === 0 ? (
        <EmptyState
          icon={<Calculator className="h-6 w-6" />}
          title="No estimates yet"
          description="Choose a template, fill in its inputs, and Vector computes the estimate client-side."
          action={
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/estimation/new')}
            >
              New estimate
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-end gap-3">
            <div className="w-56">
              <Select
                label="Room"
                placeholder="All rooms"
                value={roomFilter}
                options={roomOptions}
                onChange={(e) => setRoomFilter(e.target.value)}
              />
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" onClick={() => setRoomFilter('')}>
                Clear filter
              </Button>
            )}
            <span className="ml-auto self-center text-xs text-zinc-500">
              {filtered.length} of {summaries.length}{' '}
              {summaries.length === 1 ? 'estimate' : 'estimates'}
            </span>
          </div>

          <Table
            columns={columns}
            rows={filtered}
            rowKey={(row) => row.id}
            onRowClick={(row) =>
              navigate(`/estimation/${encodeURIComponent(row.id)}`)
            }
            empty="No estimates match the current filter."
          />
        </>
      )}
    </div>
  );
}

export default EstimationPage;
